import { useId, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { map } from './core/MapView';
import { findFonts, geofenceToFeature } from './core/mapUtil';
import { useAttributePreference } from '../common/util/preferences';
import { getVisibleGeofenceIds, GEOFENCE_FILTER_CHANGE_EVENT } from './controls/GeofenceFilterPanel';
import { useMapControlSetting } from './controls/useMapControls';

const MapGeofence = () => {
  const id = useId();

  const theme = useTheme();

  const mapGeofences = useAttributePreference('mapGeofences', true);

  const geofences = useSelector((state) => state.geofences.items);

  // Map controls panel settings - usando hook individual para melhor performance
  const showGeofencesSetting = useMapControlSetting('showGeofences', mapGeofences);
  const showGeofences = showGeofencesSetting ?? mapGeofences;

  // Estado para IDs de geofences visíveis (filtro do usuário)
  const [visibleGeofenceIds, setVisibleGeofenceIds] = useState(() => getVisibleGeofenceIds());

  // Listener para mudanças no filtro de geofences
  useEffect(() => {
    const handleFilterChange = (event) => {
      setVisibleGeofenceIds(event.detail.visibleIds);
    };

    // Inicializa com o valor do localStorage
    setVisibleGeofenceIds(getVisibleGeofenceIds());

    // Escuta eventos de mudança no filtro
    window.addEventListener(GEOFENCE_FILTER_CHANGE_EVENT, handleFilterChange);
    return () => {
      window.removeEventListener(GEOFENCE_FILTER_CHANGE_EVENT, handleFilterChange);
    };
  }, []);

  useEffect(() => {
    if (mapGeofences) {
      map.addSource(id, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
      map.addLayer({
        source: id,
        id: 'geofences-fill',
        type: 'fill',
        filter: [
          'all',
          ['==', '$type', 'Polygon'],
        ],
        paint: {
          'fill-color': ['get', 'color'],
          'fill-outline-color': ['get', 'color'],
          'fill-opacity': 0.1,
        },
      });
      map.addLayer({
        source: id,
        id: 'geofences-line',
        type: 'line',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['get', 'width'],
          'line-opacity': ['get', 'opacity'],
        },
      });
      map.addLayer({
        source: id,
        id: 'geofences-title',
        type: 'symbol',
        layout: {
          'text-field': '{name}',
          'text-font': findFonts(map),
          'text-size': 12,
        },
        paint: {
          'text-halo-color': 'white',
          'text-halo-width': 1,
        },
      });

      return () => {
        if (map.getLayer('geofences-fill')) {
          map.removeLayer('geofences-fill');
        }
        if (map.getLayer('geofences-line')) {
          map.removeLayer('geofences-line');
        }
        if (map.getLayer('geofences-title')) {
          map.removeLayer('geofences-title');
        }
        if (map.getSource(id)) {
          map.removeSource(id);
        }
      };
    }
    return () => {};
  }, [mapGeofences]);

  useEffect(() => {
    if (mapGeofences) {
      map.getSource(id)?.setData({
        type: 'FeatureCollection',
        features: Object.values(geofences)
          .filter((geofence) => {
            // Sempre oculta geofences com atributo hide
            if (geofence.attributes?.hide) return false;
            // Se não há filtro ativo, mostra todas
            if (visibleGeofenceIds === null) return true;
            // Caso contrário, verifica se está na lista de visíveis
            return visibleGeofenceIds.has(geofence.id);
          })
          .map((geofence) => geofenceToFeature(theme, geofence)),
      });
    }
  }, [mapGeofences, geofences, visibleGeofenceIds]);

  // Toggle geofence visibility based on control panel settings
  useEffect(() => {
    if (!mapGeofences || !map) return;
    try {
      const visibility = showGeofences ? 'visible' : 'none';
      if (map.getLayer('geofences-fill')) {
        map.setLayoutProperty('geofences-fill', 'visibility', visibility);
      }
      if (map.getLayer('geofences-line')) {
        map.setLayoutProperty('geofences-line', 'visibility', visibility);
      }
      if (map.getLayer('geofences-title')) {
        map.setLayoutProperty('geofences-title', 'visibility', visibility);
      }
    } catch {
      // Layers not yet created
    }
  }, [mapGeofences, showGeofences]);

  return null;
};

export default MapGeofence;
